import { Link, useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import { Compass, Loader2, RotateCw } from 'lucide-react'

import { ErrorState } from '@/components/kit/error-state'
import { Page } from '@/components/kit/page'
import { Button } from '@/components/ui/button'

/** Shown while a route's code chunk is still in flight. */
export function RoutePendingComponent() {
    return (
        <div
            role="status"
            aria-live="polite"
            className="flex items-center justify-center gap-2 px-6 py-20 text-sm text-muted-foreground">
            <Loader2 aria-hidden className="size-4 animate-spin" />
            Loading
        </div>
    )
}

/**
 * A stale tab is the common case here, not a bug.
 *
 * Route modules are hashed, so the chunk a session was going to load stops existing
 * the moment a deploy lands. The browser reports that as a generic module error, and
 * the honest fix is a reload -- but only if we say so, otherwise the operator sees
 * "Failed to fetch dynamically imported module" and files a ticket.
 */
function isStaleChunkError(error: unknown): boolean {
    if (!(error instanceof Error)) return false
    const message = error.message.toLowerCase()
    return (
        message.includes('dynamically imported module') ||
        message.includes('failed to fetch dynamically') ||
        message.includes('importing a module script failed') ||
        message.includes('error loading chunk')
    )
}

/** Rendered when a route component throws, rather than blanking the shell. */
export function RouteErrorComponent({ error, reset }: ErrorComponentProps) {
    const router = useRouter()

    if (isStaleChunkError(error)) {
        return (
            <Page>
                {/* Same skeleton as EmptyState, so a stale tab reads like every other
                    terminal state in the console. */}
                <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                    <p className="text-sm font-medium text-foreground">This tab is running an older version</p>
                    <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
                        Admin was redeployed while this tab was open, so part of the page it was about to load no longer
                        exists. Reloading picks up the new build. Nothing you have saved is affected.
                    </p>
                    <Button size="sm" onClick={() => window.location.reload()} className="mt-1">
                        <RotateCw />
                        Reload
                    </Button>
                </div>
            </Page>
        )
    }

    return (
        <Page>
            <ErrorState
                error={error}
                context="this page"
                onRetry={() => {
                    reset()
                    void router.invalidate()
                }}
            />
            <div className="flex justify-center">
                <Button variant="ghost" size="sm" asChild>
                    <Link to="/">
                        <Compass />
                        Back to home
                    </Link>
                </Button>
            </div>
        </Page>
    )
}
